import { useCallback, useEffect, useMemo, useState } from "react";
import { Crown, PencilSimple, ShieldCheck, Ticket, Trash, Users } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { API, fmtErr, inr, STATES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatCard from "../components/StatCard";
import AdminSiteContent from "../components/AdminSiteContent";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DOCS } from "./Legal";
import { normalizeSiteContent } from "../lib/siteContent";

const STATUS_CLS = {
  open: "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#B7791F]",
  in_progress: "border-[#0A2540]/30 bg-[#0A2540]/10 text-[#0A2540]",
  resolved: "border-[#008A52]/30 bg-[#008A52]/10 text-[#008A52]",
};

export default function Admin() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [offlineLicenses, setOfflineLicenses] = useState([]);
  const [licensePlan, setLicensePlan] = useState("pro");
  const [licenseMonths, setLicenseMonths] = useState("12");
  const [issuedLicense, setIssuedLicense] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [subs, setSubs] = useState(null);
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailForm, setEmailForm] = useState(null);
  const [sellerForm, setSellerForm] = useState(null);
  const [siteContent, setSiteContent] = useState(null);
  const [paymentGatewayStatus, setPaymentGatewayStatus] = useState(null);
  const [paymentGatewayForm, setPaymentGatewayForm] = useState(null);
  const [paymentGatewayError, setPaymentGatewayError] = useState("");
  const [subTarget, setSubTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState({ next: "", confirm: "" });
  const [delTarget, setDelTarget] = useState(null);
  const [subPlan, setSubPlan] = useState("pro");
  const [subMonths, setSubMonths] = useState("1");
  const [editPlan, setEditPlan] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const activePlans = useMemo(() => plans.filter((p) => p.active), [plans]);
  const paidPlans = useMemo(() => activePlans.filter((p) => p.key !== "free"), [activePlans]);

  const load = useCallback(async () => {
    if (user?.role !== "admin") return;
    try {
      const [o, u, p, t, sb, ec, sc, ol, publicContent] = await Promise.all([
        API.get("/admin/overview"),
        API.get("/admin/users"),
        API.get("/admin/plans"),
        API.get("/admin/tickets"),
        API.get("/admin/subscriptions"),
        API.get("/admin/email-config"),
        API.get("/admin/seller-config"),
        API.get("/admin/offline-licenses"),
        API.get("/public/site-content"),
      ]);
      setOverview(o.data);
      setUsers(u.data);
      setPlans(p.data);
      setOfflineLicenses(ol.data);
      setLicensePlan((current) => p.data.some((plan) => plan.key === current && plan.active && plan.key !== "free")
        ? current
        : p.data.find((plan) => plan.active && plan.key !== "free")?.key || "");
      setTickets(t.data);
      setSubs(sb.data);
      setEmailCfg(ec.data);
      setEmailForm((f) => f || { api_key: "", from_email: ec.data.from_email, from_name: ec.data.from_name, enabled: ec.data.enabled });
      setSellerForm((f) => f || { name: sc.data.name, gstin: sc.data.gstin, address: sc.data.address, state: sc.data.state });
      setSiteContent((current) => current || normalizeSiteContent(publicContent.data, DOCS));
      setThread((th) => (th ? t.data.find((x) => x.id === th.id) || null : null));
    } catch (e) {
      toast.error(fmtErr(e));
    }
    try {
      const { data } = await API.get("/admin/payment-gateway");
      setPaymentGatewayStatus(data);
      setPaymentGatewayForm((current) => current || {
        active_gateway: data.active_gateway,
        cashfree_environment: data.cashfree_environment,
        razorpay_environment: data.razorpay_environment,
        cashfree_client_id: "",
        cashfree_client_secret: "",
        razorpay_key_id: "",
        razorpay_key_secret: "",
        razorpay_webhook_secret: "",
      });
      setPaymentGatewayError("");
    } catch (e) {
      setPaymentGatewayError(fmtErr(e));
    }
  }, [user?.role]);

  useEffect(() => {
    load();
  }, [load]);

  if (user?.role !== "admin")
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2" data-testid="admin-forbidden">
        <ShieldCheck size={40} className="text-muted-foreground" />
        <p className="font-heading text-lg font-semibold">Admin access required</p>
      </div>
    );

  const setRole = async (u, role) => {
    try {
      await API.put(`/admin/users/${u.user_id}/role`, { role });
      toast.success(`${u.email} is now ${role}`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const assignSub = async () => {
    setBusy(true);
    try {
      await API.post(`/admin/users/${subTarget.user_id}/subscription`, { plan_key: subPlan, months: Number(subMonths) });
      toast.success(`Subscription updated for ${subTarget.email}`);
      setSubTarget(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelSub = async (u) => {
    try {
      await API.post(`/admin/users/${u.user_id}/subscription/cancel`);
      toast.success(`Subscription cancelled for ${u.email}`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const createOfflineLicense = async () => {
    setBusy(true);
    try {
      const response = await API.post("/admin/offline-licenses", {
        plan_key: licensePlan,
        months: Number(licenseMonths),
      });
      setIssuedLicense(response.data);
      toast.success("Offline license key created");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const revokeOfflineLicense = async (license) => {
    if (!window.confirm(`Revoke unused license ${license.key_hint}?`)) return;
    setBusy(true);
    try {
      await API.post(`/admin/offline-licenses/${license.id}/revoke`);
      toast.success("Offline license revoked");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const removePlan = async (p) => {
    if (!window.confirm(`Delete plan "${p.name}"? Users on it must be reassigned first.`)) return;
    try {
      await API.delete(`/admin/plans/${p.key}`);
      toast.success("Plan deleted");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const saveEmailCfg = async () => {
    setBusy(true);
    try {
      await API.put("/admin/email-config", {
        api_key: emailForm.api_key,
        from_email: emailForm.from_email,
        from_name: emailForm.from_name,
        enabled: emailForm.enabled,
      });
      toast.success("Email settings saved");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const sendTestEmail = async () => {
    setBusy(true);
    try {
      await API.post("/admin/email-config/test");
      toast.success("Test email sent to your inbox");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const saveSellerCfg = async () => {
    setBusy(true);
    try {
      await API.put("/admin/seller-config", sellerForm);
      toast.success("Seller details saved");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const updateSiteContent = (nextContent) => {
    setSiteContent(normalizeSiteContent(nextContent, DOCS));
  };

  const savePaymentGateway = async () => {
    setBusy(true);
    try {
      const { data } = await API.put("/admin/payment-gateway", paymentGatewayForm);
      setPaymentGatewayStatus(data);
      setPaymentGatewayForm((current) => ({
        ...current,
        cashfree_client_id: "",
        cashfree_client_secret: "",
        razorpay_key_id: "",
        razorpay_key_secret: "",
        razorpay_webhook_secret: "",
      }));
      toast.success("Payment gateway settings saved");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    if (resetPw.next.length < 8) return toast.error("Password must be at least 8 characters");
    if (resetPw.next !== resetPw.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await API.post(`/admin/users/${resetTarget.user_id}/reset-password`, { new_password: resetPw.next });
      toast.success(`Password reset for ${resetTarget.email} — their sessions were logged out`);
      setResetTarget(null);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const enterUser = async (u) => {
    const accountWindow = window.open("about:blank", "_blank");
    if (!accountWindow) {
      toast.error("Allow pop-ups for this site to enter the subscriber account.");
      return;
    }
    accountWindow.opener = null;
    try {
      const r = await API.post(`/admin/users/${u.user_id}/impersonate`);
      accountWindow.location.href = `/impersonate/${r.data.token}`;
    } catch (e) {
      accountWindow.close();
      toast.error(fmtErr(e));
    }
  };

  const deleteUser = async () => {
    setBusy(true);
    try {
      await API.delete(`/admin/users/${delTarget.user_id}`);
      toast.success(`${delTarget.email} and all their data deleted`);
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const savePlan = async () => {
    setBusy(true);
    try {
      const payload = {
        name: editPlan.name,
        price_monthly: Number(editPlan.price_monthly) || 0,
        price_yearly: Number(editPlan.price_yearly) || 0,
        invoice_limit: Number(editPlan.invoice_limit) || 0,
        features: {
          ...(editPlan.features || {}),
          branch_limit: Number(editPlan.features?.branch_limit ?? (editPlan.features?.multi_branch ? 5 : 0)),
        },
        active: !!editPlan.active,
      };
      if (editPlan.isNew) {
        await API.post("/admin/plans", payload);
        toast.success("Plan created");
      } else {
        await API.put(`/admin/plans/${editPlan.key}`, payload);
        toast.success("Plan updated");
      }
      setEditPlan(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const setTicketStatus = async (t, status) => {
    try {
      await API.put(`/admin/tickets/${t.id}/status`, { status });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await API.post(`/admin/tickets/${thread.id}/reply`, { message: reply });
      setReply("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Admin Portal</h1>
        <p className="text-sm text-muted-foreground">Users, subscriptions, plans and support tickets</p>
      </div>

      <Tabs defaultValue="overview">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="overview" data-testid="admin-overview-tab">Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-users-tab">Users</TabsTrigger>
          <TabsTrigger value="subscriptions" data-testid="admin-subscriptions-tab">Subscriptions</TabsTrigger>
          <TabsTrigger value="plans" data-testid="admin-plans-tab">Plans</TabsTrigger>
          <TabsTrigger value="offline-licenses" data-testid="admin-offline-licenses-tab">Offline Licenses</TabsTrigger>
          <TabsTrigger value="email" data-testid="admin-email-tab">Email</TabsTrigger>
          <TabsTrigger value="payment-gateway" data-testid="admin-payment-gateway-tab">Payments</TabsTrigger>
          <TabsTrigger value="site-content" data-testid="admin-site-content-tab">Website Content</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="admin-tickets-tab">Tickets</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard title="Total Users" value={overview?.users ?? "—"} icon={Users} testId="admin-stat-users" />
            <StatCard title="Paid Subscriptions" value={overview?.paid_subscriptions ?? "—"} icon={Crown} testId="admin-stat-paid" />
            <StatCard title="Open Tickets" value={overview?.open_tickets ?? "—"} icon={Ticket} testId="admin-stat-open-tickets" />
            <StatCard title="Resolved Tickets" value={overview?.resolved_tickets ?? "—"} icon={Ticket} testId="admin-stat-resolved" />
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="overflow-x-auto rounded-lg border bg-card">
            <Table data-testid="admin-users-table" className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id} data-testid={`admin-user-row-${u.email}`}>
                    <TableCell>
                      <p className="font-medium">{u.name || "—"}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={u.role === "admin" ? "border-primary/30 bg-primary/10 text-primary" : ""}>{u.role}</Badge>
                    </TableCell>
                    <TableCell className="capitalize">{u.subscription?.plan_key || "free"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {u.subscription?.expires_at ? new Date(u.subscription.expires_at).toLocaleDateString("en-IN") : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => { setSubTarget(u); setSubPlan(u.subscription?.plan_key || "pro"); }} data-testid={`admin-assign-sub-${u.email}`}>
                          <Crown size={14} className="mr-1" /> Plan
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setResetTarget(u); setResetPw({ next: "", confirm: "" }); }} data-testid={`admin-reset-pw-${u.email}`}>
                          Reset PW
                        </Button>
                        {u.role !== "admin" && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => enterUser(u)} data-testid={`admin-enter-${u.email}`}>
                              Enter account
                            </Button>
                            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDelTarget(u)} data-testid={`admin-delete-${u.email}`}>
                              Delete
                            </Button>
                          </>
                        )}
                        {u.subscription?.plan_key && u.subscription.plan_key !== "free" && (
                          <Button size="sm" variant="ghost" onClick={() => cancelSub(u)} data-testid={`admin-cancel-sub-${u.email}`}>
                            Cancel Sub
                          </Button>
                        )}
                        {u.user_id !== user.user_id && (
                          <Button size="sm" variant="ghost" onClick={() => setRole(u, u.role === "admin" ? "user" : "admin")} data-testid={`admin-toggle-role-${u.email}`}>
                            {u.role === "admin" ? "Revoke Admin" : "Make Admin"}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="subscriptions" className="mt-6 space-y-6" data-testid="admin-subscriptions-content">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard title="Active" value={subs?.counts.active ?? "—"} icon={Crown} testId="subs-stat-active" />
            <StatCard title="Expiring in 7 days" value={subs?.counts.upcoming ?? "—"} icon={Crown} testId="subs-stat-upcoming" />
            <StatCard title="Expired" value={subs?.counts.expired ?? "—"} icon={Crown} testId="subs-stat-expired" />
          </div>
          {[
            ["Active subscriptions", "active"],
            ["Expiring within 7 days", "upcoming"],
            ["Expired", "expired"],
          ].map(([title, key]) => (
            <div key={key} className="overflow-x-auto rounded-lg border bg-card" data-testid={`subs-${key}-table`}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{title}</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(subs?.[key] || []).map((s) => {
                    const subscriber = users.find((u) => u.user_id === s.user_id);
                    return (
                      <TableRow key={`${key}-${s.user_id}`}>
                        <TableCell>
                          <p className="font-medium">{s.name || "—"}</p>
                          <p className="text-xs text-muted-foreground">{s.email}</p>
                        </TableCell>
                        <TableCell className="capitalize">{s.plan_key}</TableCell>
                        <TableCell className="capitalize">{s.source || "—"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {s.expires_at ? new Date(s.expires_at).toLocaleDateString("en-IN") : "Lifetime"}
                        </TableCell>
                        <TableCell className="text-right">
                          {subscriber?.role !== "admin" && (
                            <Button size="sm" variant="outline" onClick={() => enterUser(s)} data-testid={`admin-enter-subscriber-${s.user_id}`}>
                              Enter account
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(subs?.[key] || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="p-6 text-center text-sm text-muted-foreground">None</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="offline-licenses" className="mt-6 space-y-6" data-testid="admin-offline-licenses-content">
          <div className="max-w-xl space-y-4 rounded-lg border bg-card p-6">
            <div>
              <h2 className="font-heading text-lg font-semibold tracking-tight">Issue Offline License</h2>
              <p className="text-xs text-muted-foreground">Generate a single-use key. It is displayed once; copy it and share it securely with the customer.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Plan</Label>
                <Select value={licensePlan} onValueChange={setLicensePlan}>
                  <SelectTrigger data-testid="offline-license-plan-select"><SelectValue placeholder="Select plan" /></SelectTrigger>
                  <SelectContent>
                    {paidPlans.map((plan) => <SelectItem key={plan.key} value={plan.key}>{plan.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Duration (months)</Label>
                <Input
                  type="number"
                  min="1"
                  max="120"
                  value={licenseMonths}
                  onChange={(e) => setLicenseMonths(e.target.value)}
                  data-testid="offline-license-months-input"
                />
              </div>
            </div>
            <Button onClick={createOfflineLicense} disabled={busy || !licensePlan || Number(licenseMonths) < 1 || Number(licenseMonths) > 120} data-testid="offline-license-create-button">
              {busy ? "Creating…" : "Generate License Key"}
            </Button>
          </div>

          <div className="overflow-x-auto rounded-lg border bg-card">
            <Table data-testid="admin-offline-licenses-table" className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Redeemed By</TableHead>
                  <TableHead>Redeemed</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {offlineLicenses.map((license) => {
                  const redeemedUser = users.find((u) => u.user_id === license.redeemed_by);
                  return (
                    <TableRow key={license.id} data-testid={`offline-license-row-${license.id}`}>
                      <TableCell className="font-mono text-xs">{license.key_hint}</TableCell>
                      <TableCell>{license.plan_name}</TableCell>
                      <TableCell>{license.months} months</TableCell>
                      <TableCell><Badge variant="outline" className={license.status === "available" ? "border-[#008A52]/30 bg-[#008A52]/10 text-[#008A52]" : ""}>{license.status}</Badge></TableCell>
                      <TableCell>{redeemedUser?.email || (license.redeemed_by ? "Account removed" : "—")}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{license.redeemed_at ? new Date(license.redeemed_at).toLocaleDateString("en-IN") : "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{new Date(license.created_at).toLocaleDateString("en-IN")}</TableCell>
                      <TableCell className="text-right">
                        {license.status === "available" && (
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => revokeOfflineLicense(license)} disabled={busy} data-testid={`offline-license-revoke-${license.id}`}>
                            Revoke
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {offlineLicenses.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="p-6 text-center text-sm text-muted-foreground">No offline license keys issued.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="email" className="mt-6" data-testid="admin-email-content">
          <div className="max-w-xl space-y-4 rounded-lg border bg-card p-6">
            <div>
              <h2 className="font-heading text-lg font-semibold tracking-tight">Email Configuration</h2>
              <p className="text-xs text-muted-foreground">
                Resend settings for signup, welcome, subscription, document and ticket emails.
                Sender: {emailCfg?.effective_from || "not configured"} · key: {emailCfg?.api_key_source || "none"}{emailCfg?.api_key_masked ? ` (${emailCfg.api_key_masked})` : ""}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Resend API Key</Label>
              <Input
                type="password"
                value={emailForm?.api_key || ""}
                onChange={(e) => setEmailForm((f) => ({ ...f, api_key: e.target.value }))}
                placeholder={emailCfg?.api_key_masked || "re_..."}
                data-testid="email-api-key-input"
              />
              <p className="text-xs text-muted-foreground">Leave blank to keep the current key.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>From Name</Label>
                <Input value={emailForm?.from_name || ""} onChange={(e) => setEmailForm((f) => ({ ...f, from_name: e.target.value }))} data-testid="email-from-name-input" />
              </div>
              <div className="space-y-2">
                <Label>From Email</Label>
                <Input value={emailForm?.from_email || ""} onChange={(e) => setEmailForm((f) => ({ ...f, from_email: e.target.value }))} placeholder="info@comfile360.in" data-testid="email-from-email-input" />
              </div>
            </div>
            <div className="space-y-3 border-t pt-4">
              <Label>Enabled email types</Label>
              {(emailCfg?.categories || []).map((c) => (
                <div key={c} className="flex items-center justify-between">
                  <span className="text-sm capitalize">{c.replace(/_/g, " ")}</span>
                  <Switch
                    checked={!!emailForm?.enabled?.[c]}
                    onCheckedChange={(v) => setEmailForm((f) => ({ ...f, enabled: { ...f.enabled, [c]: v } }))}
                    data-testid={`email-toggle-${c}`}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button onClick={saveEmailCfg} disabled={busy} data-testid="email-config-save">Save Email Settings</Button>
              <Button variant="outline" onClick={sendTestEmail} disabled={busy} data-testid="email-config-test">Send Test Email</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="site-content" className="mt-6" data-testid="admin-site-content-content">
          <AdminSiteContent content={siteContent} onSaved={updateSiteContent} />
        </TabsContent>

        <TabsContent value="payment-gateway" className="mt-6 space-y-5" data-testid="admin-payment-gateway-content">
          {paymentGatewayError && (
            <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              Payment gateway settings could not be loaded: {paymentGatewayError}
            </p>
          )}
          {paymentGatewayForm && (
            <>
              <section className="space-y-4 rounded-lg border bg-card p-5">
                <div>
                  <h2 className="font-heading text-lg font-semibold">Subscription payment gateway</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Choose which provider collects new paid subscriptions. Existing subscriptions continue with their original provider.
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Active provider</Label>
                    <Select
                      value={paymentGatewayForm.active_gateway}
                      onValueChange={(value) => setPaymentGatewayForm((form) => ({ ...form, active_gateway: value }))}
                    >
                      <SelectTrigger data-testid="payment-gateway-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cashfree">Cashfree</SelectItem>
                        <SelectItem value="razorpay">Razorpay</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Cashfree environment</Label>
                    <Select
                      value={paymentGatewayForm.cashfree_environment}
                      onValueChange={(value) => setPaymentGatewayForm((form) => ({ ...form, cashfree_environment: value }))}
                    >
                      <SelectTrigger data-testid="cashfree-environment-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">Sandbox</SelectItem>
                        <SelectItem value="production">Production</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Razorpay environment</Label>
                    <Select
                      value={paymentGatewayForm.razorpay_environment}
                      onValueChange={(value) => setPaymentGatewayForm((form) => ({ ...form, razorpay_environment: value }))}
                    >
                      <SelectTrigger data-testid="razorpay-environment-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="test">Test</SelectItem>
                        <SelectItem value="production">Production</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="rounded-md border bg-muted/30 p-3 text-sm" data-testid="payment-gateway-status">
                  Active gateway: <strong className="capitalize">{paymentGatewayStatus?.active_gateway || paymentGatewayForm.active_gateway}</strong>
                  <span className="ml-2 text-muted-foreground">
                    {paymentGatewayStatus?.payments_enabled ? "Configured and ready" : "Credentials incomplete"}
                  </span>
                </div>
              </section>

              <section className="space-y-4 rounded-lg border bg-card p-5">
                <div>
                  <h3 className="font-heading font-semibold">Cashfree credentials</h3>
                  <p className="text-xs text-muted-foreground">
                    Leave secret fields blank to keep saved credentials. Environment variables remain available as fallback.
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Client ID {paymentGatewayStatus?.cashfree_client_id_set ? "(configured)" : "(not configured)"}</Label>
                    <Input
                      value={paymentGatewayForm.cashfree_client_id}
                      onChange={(event) => setPaymentGatewayForm((form) => ({ ...form, cashfree_client_id: event.target.value }))}
                      autoComplete="off"
                      data-testid="cashfree-client-id-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Client Secret {paymentGatewayStatus?.cashfree_client_secret_set ? "(configured)" : "(not configured)"}</Label>
                    <Input
                      type="password"
                      value={paymentGatewayForm.cashfree_client_secret}
                      onChange={(event) => setPaymentGatewayForm((form) => ({ ...form, cashfree_client_secret: event.target.value }))}
                      autoComplete="new-password"
                      data-testid="cashfree-client-secret-input"
                    />
                  </div>
                </div>
              </section>

              <section className="space-y-4 rounded-lg border bg-card p-5">
                <div>
                  <h3 className="font-heading font-semibold">Razorpay credentials</h3>
                  <p className="text-xs text-muted-foreground">
                    Add the API key secret and webhook secret from Razorpay. Configure the webhook URL shown after saving.
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  {[
                    ["razorpay_key_id", "Key ID", "text", paymentGatewayStatus?.razorpay_key_id_set],
                    ["razorpay_key_secret", "Key Secret", "password", paymentGatewayStatus?.razorpay_key_secret_set],
                    ["razorpay_webhook_secret", "Webhook Secret", "password", paymentGatewayStatus?.razorpay_webhook_secret_set],
                  ].map(([key, label, type, isSet]) => (
                    <div key={key} className="space-y-2">
                      <Label>{label} {isSet ? "(configured)" : "(not configured)"}</Label>
                      <Input
                        type={type}
                        value={paymentGatewayForm[key]}
                        onChange={(event) => setPaymentGatewayForm((form) => ({ ...form, [key]: event.target.value }))}
                        autoComplete={type === "password" ? "new-password" : "off"}
                        data-testid={`${key.replaceAll("_", "-")}-input`}
                      />
                    </div>
                  ))}
                </div>
                <p className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
                  Razorpay webhook endpoint: <code className="select-all">/api/webhooks/razorpay</code>. Subscribe to <code>subscription.activated</code>, <code>subscription.charged</code>, <code>subscription.halted</code>, and <code>subscription.cancelled</code>.
                </p>
              </section>

              <Button onClick={savePaymentGateway} disabled={busy} data-testid="payment-gateway-save-button">
                {busy ? "Saving…" : "Save Payment Settings"}
              </Button>
            </>
          )}
        </TabsContent>

        <TabsContent value="plans" className="mt-6 space-y-3">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setEditPlan({ isNew: true, name: "", price_monthly: 0, price_yearly: 0, invoice_limit: 0, features: { statement_import: true, branch_limit: 0, multi_branch: false, invoice_email: true, custom_invoice_sender: false }, active: true })} data-testid="admin-add-plan-button">
              Add Plan
            </Button>
          </div>
          <div className="overflow-x-auto rounded-lg border bg-card">
            <Table data-testid="admin-plans-table" className="min-w-[640px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Plan</TableHead>
                  <TableHead className="text-right">Monthly</TableHead>
                  <TableHead className="text-right">Yearly</TableHead>
                  <TableHead className="text-right">Invoice Limit /mo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((p) => (
                  <TableRow key={p.key} data-testid={`admin-plan-row-${p.key}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-right tabular-nums">{inr(p.price_monthly)}</TableCell>
                    <TableCell className="text-right tabular-nums">{inr(p.price_yearly)}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.invoice_limit === 0 ? "Unlimited" : p.invoice_limit}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={p.active ? "border-[#008A52]/30 bg-[#008A52]/10 text-[#008A52]" : "border-[#E02424]/30 bg-[#E02424]/10 text-[#E02424]"}>
                        {p.active ? "Active" : "Disabled"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditPlan({ ...p, features: { ...p.features, branch_limit: p.features?.branch_limit ?? (p.features?.multi_branch ? 5 : 0), invoice_email: p.features?.invoice_email ?? true, custom_invoice_sender: p.features?.custom_invoice_sender ?? p.key !== "free" } })} data-testid={`admin-edit-plan-${p.key}`}>
                          <PencilSimple size={15} />
                        </Button>
                        {!["free", "pro", "business"].includes(p.key) && (
                          <Button size="sm" variant="ghost" onClick={() => removePlan(p)} data-testid={`admin-delete-plan-${p.key}`}>
                            <Trash size={15} className="text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="max-w-xl space-y-4 rounded-lg border bg-card p-6" data-testid="seller-config-card">
            <div>
              <h2 className="font-heading text-lg font-semibold tracking-tight">Seller GST Details</h2>
              <p className="text-xs text-muted-foreground">
                Your company details printed on subscription tax invoices (SAC 998314, 18% GST) emailed to users after every Cashfree payment.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Company / Legal Name</Label>
                <Input value={sellerForm?.name || ""} onChange={(e) => setSellerForm((f) => ({ ...f, name: e.target.value }))} data-testid="seller-name-input" />
              </div>
              <div className="space-y-2">
                <Label>GSTIN</Label>
                <Input value={sellerForm?.gstin || ""} onChange={(e) => setSellerForm((f) => ({ ...f, gstin: e.target.value }))} placeholder="27AAAAA0000A1Z5" data-testid="seller-gstin-input" />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Select value={sellerForm?.state || ""} onValueChange={(v) => setSellerForm((f) => ({ ...f, state: v }))}>
                  <SelectTrigger data-testid="seller-state-select"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>
                    {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Same-state subscribers get CGST+SGST, others IGST.</p>
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input value={sellerForm?.address || ""} onChange={(e) => setSellerForm((f) => ({ ...f, address: e.target.value }))} data-testid="seller-address-input" />
              </div>
            </div>
            <Button onClick={saveSellerCfg} disabled={busy} data-testid="seller-config-save">{busy ? "Saving…" : "Save Seller Details"}</Button>
          </div>
        </TabsContent>

        <TabsContent value="tickets" className="mt-6">
          <div className="space-y-3">
            {tickets.length === 0 ? (
              <p className="rounded-lg border bg-card p-10 text-center text-sm text-muted-foreground" data-testid="admin-tickets-empty">No support tickets.</p>
            ) : (
              tickets.map((t) => (
                <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-4" data-testid={`admin-ticket-row-${t.id}`}>
                  <button className="text-left" onClick={() => setThread(t)}>
                    <p className="font-medium hover:underline">{t.subject}</p>
                    <p className="text-xs text-muted-foreground">{t.user_email} · {new Date(t.updated_at).toLocaleString("en-IN")} · {t.messages.length} messages</p>
                  </button>
                  <Select value={t.status} onValueChange={(v) => setTicketStatus(t, v)}>
                    <SelectTrigger className={`w-36 ${STATUS_CLS[t.status]}`} data-testid={`admin-ticket-status-${t.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!subTarget} onOpenChange={(v) => !v && setSubTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="assign-subscription-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Assign Subscription</DialogTitle>
            <DialogDescription className="sr-only">Assign or extend a subscription plan for a user</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{subTarget?.email}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select value={subPlan} onValueChange={setSubPlan}>
                <SelectTrigger data-testid="assign-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {activePlans.map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Months (0 = lifetime)</Label>
              <Input type="number" min="0" max="120" value={subMonths} onChange={(e) => setSubMonths(e.target.value)} data-testid="assign-months-input" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Same-plan assignment extends from the current expiry.</p>
          <Button className="w-full" onClick={assignSub} disabled={busy} data-testid="assign-subscription-button">
            {busy ? "Saving…" : "Assign / Extend"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!issuedLicense} onOpenChange={(open) => !open && setIssuedLicense(null)}>
        <DialogContent className="max-w-md" data-testid="offline-license-issued-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">License Key Created</DialogTitle>
            <DialogDescription>This key is shown only once. Copy it now and share it securely with the customer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">{issuedLicense?.plan_name} · {issuedLicense?.months} months</p>
            <Input readOnly value={issuedLicense?.license_key || ""} className="font-mono" data-testid="offline-license-issued-key" />
            <Button
              className="w-full"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(issuedLicense.license_key);
                  toast.success("License key copied");
                } catch (e) {
                  toast.error("Could not copy automatically; select and copy the key above");
                }
              }}
              data-testid="offline-license-copy-button"
            >
              Copy License Key
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="admin-delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-destructive">Delete User</DialogTitle>
            <DialogDescription className="sr-only">Permanently delete this user and all their data</DialogDescription>
          </DialogHeader>
          <p className="text-sm">
            Permanently delete <strong>{delTarget?.email}</strong> and ALL their data — invoices, parties, items, expenses, banking, settings? This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDelTarget(null)} data-testid="admin-delete-cancel-button">Cancel</Button>
            <Button variant="destructive" onClick={deleteUser} disabled={busy} data-testid="admin-delete-confirm-button">
              {busy ? "Deleting…" : "Delete Permanently"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="admin-reset-pw-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Reset Password</DialogTitle>
            <DialogDescription className="sr-only">Set a new password for this user; all their active sessions will be logged out</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{resetTarget?.email}</p>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input type="password" value={resetPw.next} onChange={(e) => setResetPw((p) => ({ ...p, next: e.target.value }))} data-testid="admin-reset-pw-new-input" />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input type="password" value={resetPw.confirm} onChange={(e) => setResetPw((p) => ({ ...p, confirm: e.target.value }))} data-testid="admin-reset-pw-confirm-input" />
            </div>
            <p className="text-xs text-muted-foreground">The user will be logged out of all devices and must sign in with the new password. Google sign-in accounts cannot be reset.</p>
            <Button className="w-full" onClick={resetPassword} disabled={busy} data-testid="admin-reset-pw-confirm-button">
              {busy ? "Resetting…" : "Reset Password"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editPlan} onOpenChange={(v) => !v && setEditPlan(null)}>
        <DialogContent data-testid="edit-plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">{editPlan?.isNew ? "Add Plan" : `Edit Plan — ${editPlan?.name}`}</DialogTitle>
            <DialogDescription className="sr-only">Edit plan pricing and limits</DialogDescription>
          </DialogHeader>
          {editPlan && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>Name</Label>
                <Input value={editPlan.name} onChange={(e) => setEditPlan({ ...editPlan, name: e.target.value })} data-testid="plan-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Monthly Price (₹)</Label>
                <Input type="number" min="0" value={editPlan.price_monthly} onChange={(e) => setEditPlan({ ...editPlan, price_monthly: e.target.value })} data-testid="plan-monthly-input" />
              </div>
              <div className="space-y-2">
                <Label>Yearly Price (₹)</Label>
                <Input type="number" min="0" value={editPlan.price_yearly} onChange={(e) => setEditPlan({ ...editPlan, price_yearly: e.target.value })} data-testid="plan-yearly-input" />
              </div>
              <div className="space-y-2">
                <Label>Invoice Limit / month (0 = unlimited)</Label>
                <Input type="number" min="0" value={editPlan.invoice_limit} onChange={(e) => setEditPlan({ ...editPlan, invoice_limit: e.target.value })} data-testid="plan-limit-input" />
              </div>
              <div className="space-y-2">
                <Label>Bank Statement Import</Label>
                <Select
                  value={editPlan.features?.statement_import ? "yes" : "no"}
                  onValueChange={(v) => setEditPlan({ ...editPlan, features: { ...editPlan.features, statement_import: v === "yes" } })}
                >
                  <SelectTrigger data-testid="plan-statement-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Enabled</SelectItem>
                    <SelectItem value="no">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Send invoices by email</Label>
                <Select
                  value={(editPlan.features?.invoice_email ?? true) ? "yes" : "no"}
                  onValueChange={(v) => setEditPlan({
                    ...editPlan,
                    features: { ...editPlan.features, invoice_email: v === "yes" },
                  })}
                >
                  <SelectTrigger data-testid="plan-invoice-email-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Enabled</SelectItem>
                    <SelectItem value="no">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Allow custom invoice sender email</Label>
                <Select
                  value={editPlan.features?.custom_invoice_sender ? "yes" : "no"}
                  onValueChange={(v) => setEditPlan({
                    ...editPlan,
                    features: { ...editPlan.features, custom_invoice_sender: v === "yes" },
                  })}
                >
                  <SelectTrigger data-testid="plan-custom-sender-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Enabled</SelectItem>
                    <SelectItem value="no">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Additional branches (0 = main branch only)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={editPlan.features?.branch_limit ?? 0}
                  onChange={(e) => setEditPlan({
                    ...editPlan,
                    features: { ...editPlan.features, branch_limit: e.target.value },
                  })}
                  data-testid="plan-branch-limit-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editPlan.active ? "active" : "disabled"} onValueChange={(v) => setEditPlan({ ...editPlan, active: v === "active" })}>
                  <SelectTrigger data-testid="plan-active-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <Button onClick={savePlan} disabled={busy} data-testid="plan-save-button">{busy ? "Saving…" : "Save Plan"}</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!thread} onOpenChange={(v) => !v && setThread(null)}>
        <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto" data-testid="admin-ticket-thread-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">{thread?.subject}</DialogTitle>
            <DialogDescription className="sr-only">Ticket thread with reply</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {thread?.messages.map((m, i) => (
              <div key={i} className={`rounded-md border p-3 text-sm ${m.from === "admin" ? "ml-8 border-primary/30 bg-primary/5" : "mr-8 bg-muted/40"}`}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {m.from === "admin" ? "Support Team" : thread.user_email} · {new Date(m.at).toLocaleString("en-IN")}
                </p>
                <p className="whitespace-pre-wrap">{m.message}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to customer…" data-testid="admin-ticket-reply-input" />
            <Button onClick={sendReply} disabled={busy} data-testid="admin-ticket-reply-button">Send</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
